var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box, IconButton, Divider as MuiDivider, styled, Tab, Tabs, Tooltip, Typography, } from '@mui/material';
import React from 'react';
import { globalHeaderHeightNormal, globalHeaderHeightSmall } from '../GlobalHeader/constants';
import { globalInfoHeight } from '../GlobalInfo/constants';
import { globalMenuWidthFull, globalMenuWidthSmall } from '../GlobalMenu/constants';
import { useCommonWebTranslation, useHoverSupported, useSmallDevice } from '../hooks';
import { themeColors } from '../JuvareTheme';
import { GlobalTabDivider } from './components/GlobalTabDivider';
import { GlobalTabLabel } from './components/GlobalTabLabel';
import { GlobalTabsMenu } from './components/GlobalTabsMenu';
import { globalTabsHeight } from './constants';
export const GlobalTabsBar = (props) => {
    var _a;
    const { t } = useCommonWebTranslation();
    const handleChange = (event, tabPath) => {
        const tab = props.tabs.find((tab) => tab.path === tabPath);
        if (tab) {
            props.onOpenTab(tab);
        }
    };
    const smallDevice = useSmallDevice();
    const hoverSupported = useHoverSupported() && !smallDevice;
    return (React.createElement(Box, { display: 'flex', justifyContent: 'space-between', sx: [
            {
                position: 'fixed',
                top: (smallDevice ? globalHeaderHeightSmall : globalHeaderHeightNormal) + (props.infoOpen ? globalInfoHeight : 0) + 'px',
                right: 0,
                left: 0,
                height: globalTabsHeight,
                paddingLeft: 12 +
                    (props.menuOpen && hoverSupported && props.menuMode !== 'icon-and-label' ? globalMenuWidthSmall : 0) +
                    (props.menuOpen && hoverSupported && props.menuMode === 'icon-and-label' ? globalMenuWidthFull : 0) +
                    'px',
                paddingRight: '4px',
                display: 'flex',
                overflow: 'hidden',
                alignItems: 'center',
                gap: 1,
                backgroundColor: themeColors.surface.primary,
                boxShadow: (theme) => theme.shadows[2],
                color: themeColors.text.link,
                transition: (theme) => theme.transitions.create('padding-left') + ',' + theme.transitions.create('margin-right', { duration: 400 }),
                zIndex: (theme) => theme.zIndex.appBar - 4,
                '@media print': {
                    display: 'none',
                },
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ], "data-test": 'tabs-bar' },
        props.tabs.length > 0 && (React.createElement(Tabs, { variant: 'scrollable', scrollButtons: true, allowScrollButtonsMobile: true, value: (_a = props.selectedTabPath) !== null && _a !== void 0 ? _a : false, onChange: handleChange, TabIndicatorProps: {
                sx: { opacity: 0 },
            }, TabScrollButtonProps: {
                sx: {
                    height: '28px',
                    width: '28px',
                    border: themeColors.border.main,
                    borderStyle: 'solid',
                    borderWidth: '2px',
                    borderRadius: '28px',
                    color: themeColors.text.link,
                },
            }, sx: {
                width: '100%',
                minHeight: 'initial',
                maxHeight: '46px',
                transition: 'opacity 0.5 ease',
                alignItems: 'center',
                '& button': { borderRadius: 1, transition: '0.3s' },
                '& button:hover': { backgroundColor: themeColors.accent.blue.alpha },
                '& button.Mui-selected': { backgroundColor: themeColors.primary.surface },
                '& button.Mui-disabled': { opacity: 1 },
            } }, props.tabs.map((tab) => [
            React.createElement(StyledTab, { key: tab.path, value: tab.path, label: React.createElement(GlobalTabLabel, { tabs: props.tabs, selectedTabPath: props.selectedTabPath, tab: tab, onCloseTab: props.onCloseTab }), onKeyDown: (event) => {
                    var _a;
                    const target = event.target;
                    if (!event.shiftKey && event.key === 'Tab' && target.nodeName === 'BUTTON') {
                        event.preventDefault();
                        (_a = target.querySelector('[role=button]')) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }, "data-test": tab['data-test'] }),
            React.createElement(GlobalTabDivider, { key: 'tabDivider' }),
        ]))),
        props.tabs.length <= 0 && (React.createElement(Box, { display: 'flex', width: '100%', justifyContent: 'center' },
            React.createElement(Typography, { variant: 'subtitle2' }, t('globalTabsBar.newlyOpenedTabsWillAppearHere')))),
        props.tabs.length > 0 && (React.createElement(Box, { display: 'flex' },
            React.createElement(Divider, { orientation: 'vertical' }),
            props.additionalActions &&
                props.additionalActions.map((_a) => {
                    var { onClick } = _a, item = __rest(_a, ["onClick"]);
                    return (React.createElement(React.Fragment, { key: item.label },
                        React.createElement(Tooltip, { title: item.label },
                            React.createElement(IconButton, { size: 'medium', sx: { color: themeColors.text.link }, "aria-label": item.label, onClick: onClick
                                    ? (event) => {
                                        event.preventDefault();
                                        onClick();
                                    }
                                    : undefined, "data-test": item['data-test'] }, item.icon))));
                }),
            React.createElement(GlobalTabsMenu, { tabs: props.tabs, onOpenTab: props.onOpenTab, onCloseAllTabs: props.onCloseAllTabs })))));
};
const StyledTab = styled(Tab)(() => ({
    maxWidth: '300px',
    minHeight: 'initial',
    maxHeight: '40px',
    padding: '3px 8px',
    alignSelf: 'center',
    color: themeColors.text.link,
}));
const Divider = styled(MuiDivider)(() => ({
    alignSelf: 'center',
    height: '24px',
    margin: '0px 8px',
}));
